"use client";

import { useEffect, useState } from "react";
import { useTranslations } from "next-intl";
import { Coins } from "lucide-react";

import { Link } from "@/i18n/navigation";
import { withErrorBoundary } from "@/shared/lib/error-boundary";
import { isHiddenInMvp } from "@/shared/lib/launch-mode";
import { isTauri, getTauriVersion } from "@/shared/lib/tauri";
import { Badge } from "@/shared/ui/data-display/badge";
import { Button } from "@/shared/ui/buttons/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/shared/ui/overlays/tooltip";
import { SimpleIcon } from "@/shared/ui/simple-icon";
import { DonateModal } from "@/widgets/marketing/donate-modal";

const marketingLinks: Array<{ key: string; href: string; tauriHidden?: boolean }> = [
  { key: "links.home", href: "/", tauriHidden: true },
  { key: "links.about", href: "/about", tauriHidden: true },
  { key: "links.faq", href: "/faq", tauriHidden: true },
  { key: "links.studio", href: "/studio", tauriHidden: true },
  { key: "links.privacy", href: "/privacy", tauriHidden: true },
  { key: "links.status", href: "/status", tauriHidden: true },
  { key: "links.support", href: "/support" },
  { key: "links.terms", href: "/terms", tauriHidden: true },
  { key: "links.cookies", href: "/cookies", tauriHidden: true },
];

const socialLinks = [
  { name: "GitHub", href: "https://github.com/RaspizDIYs/goodluckv2", icon: "github" },
  { name: "Discord", href: "https://discord.gg/dmx5GqHDcN", icon: "discord" },
  { name: "Telegram", href: "https://t.me/goodluck_tg", icon: "telegram" },
  { name: "VK", href: "https://vk.ru/gltournaments", icon: "vk" },
];

function FooterBase() {
  const tUi = useTranslations("Ui");
  const t = useTranslations("Footer");
  const tDonate = useTranslations("Marketing.About.donate");
  const [tauriVersion, setTauriVersion] = useState<string | null>(null);
  const [donateOpen, setDonateOpen] = useState(false);

  useEffect(() => {
    if (!isTauri) return;
    getTauriVersion().then(setTauriVersion);
  }, []);

  const buildIdRaw = process.env["NEXT_PUBLIC_BUILD_ID"] ?? "dev";
  const buildId =
    buildIdRaw.length > 12 ? `${buildIdRaw.slice(0, 7)}` : buildIdRaw;

  // KAN-1671: версия релиза (0.114.3) рядом с sha — по одному sha владелец не
  // понимает, какой это релиз. Пусто в локальной сборке — бейдж не показываем.
  const buildVersion = (process.env["NEXT_PUBLIC_BUILD_VERSION"] ?? "")
    .trim()
    .replace(/^release-/, "");

  const buildDateRaw = process.env["NEXT_PUBLIC_BUILD_DATE"] ?? "";
  const buildDate = (() => {
    if (!buildDateRaw) return "";
    if (/^\d{4}-\d{2}-\d{2}/.test(buildDateRaw)) return buildDateRaw.slice(0, 10);
    const d = new Date(buildDateRaw);
    if (Number.isNaN(d.getTime())) return "";
    return d.toISOString().slice(0, 10);
  })();

  const visibleLinks = (isTauri
    ? marketingLinks.filter((l) => !l.tauriHidden)
    : marketingLinks
  ).filter((l) => !isHiddenInMvp(l.href));

  return (
    <footer
      className="gl-enter bg-background border-t"
      style={
        {
          "--gl-enter-from": "translateY(50px)",
          "--gl-enter-delay": "0.2s",
        } as React.CSSProperties
      }
    >
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-6">
        {/* Links block */}
        <nav className="w-full rounded-2xl bg-black/6 dark:bg-white/4 px-6 py-5 flex flex-wrap items-center justify-center gap-x-6 gap-y-3">
          {visibleLinks.map((link) => (
            <Link
              key={link.href}
              href={link.href}
              // Футер есть на каждой странице, а его ссылки (правила, cookies, FAQ,
              // статус) открывают редко: предзагрузка давала по два RSC-запроса на
              // ссылку при каждом заходе, первый роутер отменял — отсюда «Не удалось
              // загрузить Fetch» в консоли. Переход при клике не меняется.
              prefetch={false}
              className="text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              {t(link.key)}
            </Link>
          ))}
        </nav>

        {/* Bottom block: copyright + disclaimer + socials */}
        <div className="rounded-2xl bg-black/6 dark:bg-white/4 px-6 py-6">
          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-6">
            {/* Left: copyright + disclaimer */}
            <div className="flex-1 min-w-0 space-y-2">
              {/* Год берётся при сборке страницы; в первые дни января кэш
                  может показать прошлый — расхождение гидратации тут безвредно. */}
              <p className="text-sm text-muted-foreground" suppressHydrationWarning>
                {t("copyright", { year: new Date().getFullYear() })}
              </p>
              <p className="text-xs text-muted-foreground leading-relaxed max-w-3xl">
                GoodLuck isn&apos;t endorsed by Riot Games and doesn&apos;t
                reflect the views or opinions of Riot Games or anyone officially
                involved in producing or managing Riot Games properties. Riot
                Games, and all associated properties are trademarks or registered
                trademarks of Riot Games, Inc.
              </p>
            </div>

            {/* Right: made with + socials + build */}
            <div className="flex flex-col items-center md:items-end gap-3 shrink-0">
              <p className="text-sm text-muted-foreground">
                {t("madeWith")}{" "}
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Link
                        href="/admin"
                        prefetch={false}
                        className="inline-block transition-transform hover:scale-125 hover:text-red-500"
                      >
                        ❤️
                      </Link>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>{t("adminPanel")}</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>{" "}
                {t("forEsports")}
              </p>

              <div className="flex items-center gap-2">
                {socialLinks.map((link) => (
                  <a
                    key={link.name}
                    href={link.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    title={link.name}
                    aria-label={link.name}
                    className="h-9 w-9 rounded-lg bg-background hover:bg-muted transition-colors flex items-center justify-center"
                  >
                    <SimpleIcon icon={link.icon} size={20} />
                  </a>
                ))}
              </div>

              <div className="flex flex-wrap items-center justify-center gap-2">
                {buildVersion ? (
                  <Badge variant="secondary" className="text-[10px] py-0.5">
                    v{buildVersion}
                  </Badge>
                ) : null}
                <Badge variant="secondary" className="text-[10px] py-0.5">
                  {buildId}
                </Badge>
                {buildDate ? (
                  <Badge variant="secondary" className="text-[10px] py-0.5">
                    {buildDate}
                  </Badge>
                ) : null}
                {tauriVersion ? (
                  <Badge variant="secondary" className="text-[10px] py-0.5">
                    v{tauriVersion}
                  </Badge>
                ) : null}
              </div>
            </div>
          </div>
        </div>

        {/* Separate payments + donate block under copyright */}
        <div className="rounded-2xl bg-black/6 dark:bg-white/4 px-6 py-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div className="flex-1 flex justify-center">
            <span className="h-8 w-auto flex items-center justify-center opacity-80">
              <img
                src="/logo/paysys.svg"
                alt={tUi("footer.sposobyOplaty")}
                width={318}
                height={40}
                className="h-8 w-auto object-contain"
              />
            </span>
          </div>
          <div className="flex justify-center md:justify-end">
            <Button
              size="sm"
              className="rounded-full gap-1.5 px-4 py-2"
              onClick={() => setDonateOpen(true)}
            >
              <Coins className="h-4 w-4" />
              {tDonate("button")}
            </Button>
          </div>
        </div>

        <DonateModal open={donateOpen} onOpenChange={setDonateOpen} />
      </div>
    </footer>
  );
}

const Footer = withErrorBoundary(FooterBase);
export default Footer;
export { Footer };
